import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, MapPin, Users, Star, X, BadgeCheck, FileText, ChevronLeft, ChevronRight, Eye, MessageCircle, Clock, Lock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { getCities, getDistricts, getDistrictsWithVenues, getVenues, createQuoteRequest } from '../services/api';
import QuoteRequestDialog from '../components/QuoteRequestDialog';
import FavoriteButton from '../components/FavoriteButton';
import AuthModal from '../components/AuthModal';
import { fixImageUrl } from '../utils/imageHelper';
import LazyImage from '../components/LazyImage';
import VenueImage from '../components/VenueImage';
import { getVenueUrl } from '../utils/slugHelper';
import AdSenseAd from '../components/AdSenseAd';
import { VenueSkeletonGrid } from '../components/VenueSkeleton';
import QuoteWizard from '../components/QuoteWizard';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cloudinary URL Optimizasyonu - Resim boyutunu ve kalitesini optimize eder
const optimizeImageUrl = (url, width = 400) => {
  if (!url || typeof url !== 'string') return '';
  
  // Cloudinary URL'lerini optimize et
  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    // Zaten transform varsa dokunma
    const afterUpload = url.split('/upload/')[1];
    if (afterUpload && !afterUpload.startsWith('v')) {
      return url; // Zaten optimize edilmiş
    }
    // Transform ekle: width, auto quality, auto format (webp/avif), progressive
    return url.replace('/upload/', `/upload/w_${width},q_auto,f_auto,fl_progressive/`);
  }
  
  return url;
};

// Quick Quote Modal Component
const QuickQuoteModal = ({ venue, isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  if (!isOpen) return null;

  const handleWizardSubmit = async (wizardData) => {
    setIsSubmitting(true);
    
    const eventTypeLabels = {
      'isteme-nisan': 'İsteme & Nişan',
      'kina': 'Kına',
      'dogum-gunu': 'Doğum Günü',
      'bebek-mevlidi': 'Bebek Mevlidi',
      'etkinlik': 'Etkinlik'
    };
    
    const cateringLabels = {
      'kendim': 'İkramlıkları kendim getireceğim',
      'ikramli': 'İkramlı İstiyorum'
    };
    
    const photographyLabels = {
      'yok': 'İstemiyorum',
      'fotograf': 'Fotoğraf',
      'hikaye': 'Hikaye',
      'fotograf-hikaye': 'Fotoğraf + Hikaye',
      'fotograf-video': 'Fotoğraf + Video',
      'video': 'Video'
    };
    
    const serviceLabels = {
      'garson': 'Garson Olsun',
      'kendimiz': 'Servisi kendimiz yaparız'
    };

    try {
      const formatMonth = (monthValue) => {
        const [month, year] = monthValue.split('-');
        const monthNames = ['Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran', 
                            'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'];
        return `${monthNames[parseInt(month) - 1]} ${year}`;
      };

      const notes = `Talep Detaylari
—
Organizasyon: ${eventTypeLabels[wizardData.eventType] || wizardData.eventType}
Kisi Sayisi: ${wizardData.guestCount}
Ikram: ${cateringLabels[wizardData.catering] || wizardData.catering}
Fotograf / Video: ${photographyLabels[wizardData.photography] || wizardData.photography}
Servis: ${serviceLabels[wizardData.service] || wizardData.service}
Planlanan Ay: ${formatMonth(wizardData.eventMonth)}`;

      const quoteData = {
        venueId: venue.id,
        venueName: venue.name,
        fullName: wizardData.fullName,
        phone: wizardData.phone,
        email: '',
        eventType: eventTypeLabels[wizardData.eventType] || wizardData.eventType,
        eventDate: wizardData.eventMonth,
        guestCount: parseInt(wizardData.guestCount.split('-')[0]) || 50,
        notes: notes
      };

      await createQuoteRequest(quoteData);
      
      toast({
        title: 'Talebiniz Alındı! 🎉',
        description: 'Mekan sahibi en kısa sürede size dönüş yapacaktır.',
      });
      
      onClose();
    } catch (error) {
      console.error('Error creating quote request:', error);
      toast({
        title: 'Hata!',
        description: error.response?.data?.detail || 'Teklif talebi oluşturulamadı.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-5 pt-5 pb-3 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
          <h3 className="text-lg font-bold text-gray-900 pr-8">{venue.name}</h3>
          <div className="flex items-center gap-1.5 mt-1 text-emerald-600">
            <Clock className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Genellikle 1 saat içinde yanıt verir</span>
          </div>
        </div>
        
        {/* Wizard Form */}
        <div className="p-4">
          <QuoteWizard 
            venue={venue} 
            onSubmit={handleWizardSubmit}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

// Minimal Venue Card Component - Yeni Tasarım (Memoized for performance)
const VenueCard = memo(({ venue, navigate, onAuthRequired, venueIndex = 0 }) => {
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const images = venue.images || [];
  // Vitrin fotoğrafı (featuredImageIndex) varsa onu kullan, yoksa ilk fotoğrafı
  // Vitrin fotoğrafı (featuredImageIndex) varsa onu kullan, yoksa ilk fotoğrafı
  const featuredIndex = venue.featuredImageIndex || 0;
  const mainImage = images[featuredIndex] || images[0] || '/placeholder-venue.jpg';
  
  // Check if venue has active campaigns
  const hasActiveCampaign = venue.activeCampaigns && venue.activeCampaigns.length > 0;
  
  const isPro = venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium';
  
  // KVKK: Mekan sahipli mi kontrol et
  const isVenueClaimed = venue.isClaimed === true || 
                         (venue.ownerId && venue.ownerId !== '') || 
                         isPro;
  
  // Calculate minimum package price (with discount if campaign exists)
  const getMinPrice = () => {
    if (!venue.packages || venue.packages.length === 0) return null;
    const pricesWithValue = venue.packages.filter(p => p.price > 0).map(p => p.price);
    if (pricesWithValue.length === 0) return null;
    return Math.min(...pricesWithValue);
  };
  const minPrice = getMinPrice();
  
  // Get campaign discount info
  const getCampaignDiscount = () => {
    if (!hasActiveCampaign) return null;
    const campaign = venue.activeCampaigns[0];
    return {
      name: campaign.campaignName,
      discountType: campaign.discountType,
      discountValue: campaign.discountValue
    };
  };
  const campaignDiscount = getCampaignDiscount();
  
  // Yıldız rating'i görsel olarak göster
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Star key={i} className="w-4 h-4 fill-amber-400/50 text-amber-400" />
        );
      } else {
        stars.push(
          <Star key={i} className="w-4 h-4 text-gray-300" />
        );
      }
    }
    return stars;
  };

  const optimizedMainImage = optimizeImageUrl(fixImageUrl(mainImage), 500);
    
  return (
    <div className={`relative ${hasActiveCampaign ? 'p-[3px] rounded-[26px] bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 animate-gradient-x' : ''}`}>
      <div 
        className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
        onClick={() => navigate(getVenueUrl(venue))}
      >
        {/* Fotoğraf Alanı - Optimize edilmiş */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          <img 
            src={optimizedMainImage}
            alt={venue.name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading={venueIndex < 4 ? "eager" : "lazy"}
            decoding={venueIndex < 4 ? "sync" : "async"}
            fetchPriority={venueIndex < 2 ? "high" : "auto"}
          />
          
          {/* Kampanya Badge - Sol üst (öncelikli) - Sadece sahipli mekanlarda göster */}
          {venue.isClaimed && hasActiveCampaign && campaignDiscount && (
            <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-red-500 to-pink-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg animate-pulse">
              🔥 {campaignDiscount.discountType === 'percentage' 
                ? `%${campaignDiscount.discountValue} İndirim!`
                : `₺${campaignDiscount.discountValue?.toLocaleString('tr-TR')} İndirim!`}
            </div>
          )}
          
          {/* Fiyat Badge - Sol üst (kampanya yoksa) - Sadece sahipli mekanlarda göster */}
          {venue.isClaimed && !hasActiveCampaign && minPrice && (
            <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
              ₺{minPrice.toLocaleString('tr-TR')}&apos;den başlayan
            </div>
          )}
          
          {/* Paket sayısı badge - Fiyat ve kampanya yoksa göster - Sadece sahipli mekanlarda */}
          {venue.isClaimed && !hasActiveCampaign && !minPrice && venue.packages && venue.packages.length > 0 && (
            <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
              {venue.packages.length} Paket
            </div>
          )}
          
          {/* Randevu Açık Badge - Sol üst (sadece randevu açıksa ve diğer badge'ler yoksa) */}
          {venue.visitSettings?.enabled && !hasActiveCampaign && !minPrice && (!venue.packages || venue.packages.length === 0) && (
            <div className="absolute top-3 left-3 z-10 bg-purple-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold shadow-lg flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Randevu
            </div>
          )}

          {/* Randevu Badge - Sağ alt köşe (favori butonunun altında, her zaman görünür) */}
          {venue.visitSettings?.enabled && (
            <div className="absolute bottom-3 right-3 z-10 bg-purple-600/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-[10px] font-semibold shadow-md flex items-center gap-1">
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Randevu
            </div>
          )}
          
          {/* Favori Butonu - Sağ üst */}
          <div className="absolute top-3 right-3 z-10">
            <FavoriteButton 
              venueId={venue.id}
              venueName={venue.name}
              venueSlug={venue.slug}
              onAuthRequired={onAuthRequired}
              size="small"
            />
          </div>
        </div>
        
        {/* İçerik Alanı */}
        <div className="p-4 sm:p-5">
          {/* Mekan Adı + Mavi Tik */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-bold text-lg text-gray-900 truncate">
              {venue.name}
            </h3>
            {isPro && (
              <BadgeCheck className="w-5 h-5 text-blue-500 fill-blue-100 flex-shrink-0" title="Doğrulanmış Mekan" />
            )}
          </div>
          
          {/* Rating - Google'dan veya manuel */}
          {(venue.googleRating || venue.rating) && (venue.googleRating > 0 || venue.rating > 0) && (
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-gray-900">{(venue.googleRating || venue.rating).toFixed(1)}</span>
              <div className="flex items-center gap-0.5">
                {renderStars(venue.googleRating || venue.rating)}
              </div>
              {(venue.googleReviewCount || venue.reviewCount) > 0 && (
                <span className="text-gray-500 text-sm">({venue.googleReviewCount || venue.reviewCount})</span>
              )}
            </div>
          )}
          
          {/* Konum */}
          <div className="flex items-center gap-1.5 text-gray-500 mb-3">
            <MapPin className="w-4 h-4 text-pink-500 flex-shrink-0" />
            <span className="text-sm truncate">{venue.districtName}</span>
          </div>
          
          {/* Kampanya ismi */}
          {hasActiveCampaign && campaignDiscount && (
            <div className="mb-3 text-xs font-medium text-pink-600 bg-pink-50 px-2 py-1 rounded-lg truncate">
              🎁 {campaignDiscount.name}
            </div>
          )}
          
          {/* Butonlar */}
          <div className="flex flex-col gap-2">
            {/* Akıllı Paket aktif mekanlar için özel buton */}
            {venue.smartPricingEnabled && (venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium') && (
              <button 
                className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600 text-white hover:from-rose-600 hover:via-pink-600 hover:to-purple-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                data-testid="smart-package-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(getVenueUrl(venue) + '#smart-package-builder');
                }}
              >
                <Sparkles className="w-4 h-4" />
                Hemen Paketini Olustur
              </button>
            )}
            {/* Teklif Al - Smart pricing olmayan Pro/Premium mekanlarda */}
            {!venue.smartPricingEnabled && (venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium') && venue.quoteFormEnabled !== false && (
              <button 
                className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-md hover:shadow-lg"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowQuoteModal(true);
                }}
              >
                <MessageCircle className="w-4 h-4" />
                Teklif Al
              </button>
            )}
            
            {/* Detayları Gör Butonu */}
            <button 
              className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-1 ${
                hasActiveCampaign 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700' 
                  : 'border-2 border-gray-200 text-pink-600 hover:border-pink-300 hover:bg-pink-50'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(getVenueUrl(venue));
              }}
            >
              {hasActiveCampaign ? 'Kampanyayı Gör' : 'Detayları Gör'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Quick Quote Modal */}
      <QuickQuoteModal 
        venue={venue}
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if venue data changes
  return prevProps.venue.id === nextProps.venue.id &&
         prevProps.venue.rating === nextProps.venue.rating &&
         prevProps.venue.activeCampaigns?.length === nextProps.venue.activeCampaigns?.length;
});

// Display name for debugging
VenueCard.displayName = 'VenueCard';

// Image Carousel Component for Venue Cards (eski - backup için tutuyoruz)
const VenueImageCarousel = ({ venue, hasCampaign, navigate, onAuthRequired, venueIndex = 0 }) => {
  // Use featuredImageIndex if available, otherwise start at 0
  const initialIndex = venue.featuredImageIndex !== undefined && venue.featuredImageIndex !== null ? venue.featuredImageIndex : 0;
  const [currentImageIndex, setCurrentImageIndex] = useState(initialIndex);
  const images = venue.images || [];
  const isLastImage = currentImageIndex === images.length - 1;
  
  // KVKK: Mekan sahipli mi kontrol et
  const isPro = venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium';
  const isVenueClaimed = venue.isClaimed === true || 
                         (venue.ownerId && venue.ownerId !== '') || 
                         isPro;

  const nextImage = (e) => {
    e.stopPropagation();
    if (currentImageIndex < images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const currentImage = fixImageUrl(images[currentImageIndex] || images[0]);
  const optimizedCurrentImage = optimizeImageUrl(currentImage, 500);

  return (
    <div 
      className="relative aspect-[4/3] overflow-hidden group/carousel bg-gray-100"
    >
      {/* Current Image - Optimize edilmiş */}
      <img 
        src={optimizedCurrentImage}
        alt={`${venue.name} - ${venue.cityName} ${venue.districtName}'de Davet Evi | Nişan ve Organizasyon`}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500"
        loading={venueIndex < 4 ? "eager" : "lazy"}
        decoding={venueIndex < 4 ? "sync" : "async"}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />
      
      {/* Navigation Arrows - Show on hover */}
      {images.length > 1 && (
        <>
          {currentImageIndex > 0 && (
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 opacity-0 group-hover/carousel:opacity-100 transition-opacity shadow-lg z-20"
            >
              <ChevronLeft className="w-5 h-5 text-gray-800" />
            </button>
          )}
          
          {currentImageIndex < images.length - 1 && (
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 opacity-0 group-hover/carousel:opacity-100 transition-opacity shadow-lg z-20"
            >
              <ChevronRight className="w-5 h-5 text-gray-800" />
            </button>
          )}
        </>
      )}
      
      {/* Image Indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
          {images.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 rounded-full transition-all ${
                idx === currentImageIndex 
                  ? 'w-6 bg-white' 
                  : 'w-1.5 bg-white/50'
              }`}
            />
          ))}
        </div>
      )}

      {/* Top Left Badges */}
      <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
        {hasCampaign && (
          <div className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg">
            <img 
              src="/assets/logo/logo-icon.svg" 
              alt="DavetEviBul" 
              className="w-5 h-5 bg-white rounded-full p-0.5"
            />
            <span className="font-bold text-white text-sm">Özel Fiyat</span>
          </div>
        )}
        {venue.packages && venue.packages[0]?.discountPercentage && (
          <div className="bg-gradient-to-r from-red-600 to-pink-600 rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-lg">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z"/>
            </svg>
            <span className="font-bold text-white text-sm">%{Math.round(venue.packages[0].discountPercentage)}</span>
          </div>
        )}
      </div>
      
      {/* Top Right - Rating and Favorite */}
      <div className="absolute top-3 right-3 flex flex-col items-end gap-2 z-10">
        <FavoriteButton 
          venueId={venue.id}
          venueName={venue.name}
          venueSlug={venue.slug}
          onAuthRequired={onAuthRequired}
          size="small"
        />
        {/* Rating Badge */}
        {(venue.googleRating || venue.rating) && (venue.googleRating > 0 || venue.rating > 0) && (
          <div className="bg-white/95 backdrop-blur-sm rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 shadow-md border border-white/20">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            <span className="font-semibold text-gray-900 text-sm">{(venue.googleRating || venue.rating).toFixed(1)}</span>
            {(venue.googleReviewCount || venue.reviewCount) > 0 && (
              <span className="text-xs text-gray-500 font-medium">({venue.googleReviewCount || venue.reviewCount})</span>
            )}
          </div>
        )}
      </div>
      
      {/* Bottom Overlay Info */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-white z-10">
        <p className="text-xs sm:text-sm font-medium mb-1">{venue.districtName}</p>
        <h3 className="font-bold text-lg sm:text-xl mb-2 flex items-center gap-2 break-words">
          <span className="break-words">{venue.name}</span>
          {(venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium') && (
            <BadgeCheck className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 fill-blue-100 flex-shrink-0" title="Doğrulanmış Mekan" />
          )}
        </h3>
      </div>
    </div>
  );
};

const VenuesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [selectedCity, setSelectedCity] = useState(searchParams.get('cityId') || '');
  
  // Initialize selectedDistricts from URL query param
  const [selectedDistricts, setSelectedDistricts] = useState(() => {
    // Support both districtIds (multiple) and districtId (single)
    const districtIds = searchParams.get('districtIds');
    const singleDistrictId = searchParams.get('districtId');
    
    if (districtIds) {
      return districtIds.split(',');
    } else if (singleDistrictId) {
      return [singleDistrictId];
    }
    return [];
  });
  
  // District search state
  const [districtSearch, setDistrictSearch] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Infinite scroll states
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalVenues, setTotalVenues] = useState(0);
  const ITEMS_PER_PAGE = 20;
  const loadMoreRef = useRef(null);
  
  // Quote request dialog
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedVenueForQuote, setSelectedVenueForQuote] = useState(null);
  
  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Autocomplete states
  const [cityInput, setCityInput] = useState('');
  const [districtInput, setDistrictInput] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const cityInputRef = useRef(null);
  const districtInputRef = useRef(null);

  // AdSense settings state
  const [adsenseSettings, setAdsenseSettings] = useState({
    adsense_code: '',
    adsense_enabled: false,
    adsense_frequency: 6
  });

  // Memoized navigate handler for VenueCard
  const handleVenueNavigate = useCallback((url) => {
    sessionStorage.setItem('venuesPageScroll', window.scrollY.toString());
    navigate(url, { 
      state: { 
        from: window.location.pathname + window.location.search,
        selectedCity,
        selectedDistricts
      }
    });
  }, [navigate, selectedCity, selectedDistricts]);

  // Memoized auth required handler
  const handleAuthRequired = useCallback(() => {
    setShowAuthModal(true);
  }, []);

  // Parse URL query params and update state when they change
  useEffect(() => {
    // Handle city from query param (by name)
    const cityParam = searchParams.get('city');
    if (cityParam && cities.length > 0) {
      const city = cities.find(c => c.name === cityParam);
      if (city) {
        // Only update if different
        if (selectedCity !== city.id) {
          setSelectedCity(city.id);
          setCityInput(city.name);
        }
      }
    }
    
    // Handle district from query param (by name or slug)
    const districtParam = searchParams.get('district');
    const districtSlugParam = searchParams.get('districtSlug');
    
    if ((districtParam || districtSlugParam) && districts.length > 0) {
      let district;
      
      if (districtSlugParam) {
        district = districts.find(d => d.slug === districtSlugParam);
      } else if (districtParam) {
        district = districts.find(d => d.name === districtParam);
      }
      
      if (district) {
        const newDistrictIds = [district.id];
        const currentIds = [...selectedDistricts].sort().join(',');
        const newIds = [...newDistrictIds].sort().join(',');
        
        if (currentIds !== newIds) {
          setSelectedDistricts(newDistrictIds);
          setDistrictInput(district.name);
          
          // Also set the parent city
          if (district.cityId && !selectedCity && cities.length > 0) {
            const parentCity = cities.find(c => c.id === district.cityId);
            if (parentCity) {
              setSelectedCity(parentCity.id);
              setCityInput(parentCity.name);
            }
          }
        }
      }
    }
  }, [searchParams, cities, districts]);

  // Parse city from URL pathname or query param
  useEffect(() => {
    // Check if URL is a city page (e.g., /bursa-davet-evleri)
    if (location.pathname.endsWith('-davet-evleri') && location.pathname !== '/davet-evleri') {
      const citySlug = location.pathname.slice(1, -13); // Remove '/' and '-davet-evleri'
      
      if (cities.length > 0) {
        const city = cities.find(c => c.slug === citySlug);
        
        if (city && !selectedCity) {
          setSelectedCity(city.id);
          setCityInput(city.name);
          setPage(1);
          setFilteredVenues([]);
        }
      }
    }
    // Check city from query param (by name or slug)
    else if ((searchParams.get('city') || searchParams.get('citySlug')) && cities.length > 0) {
      let city;
      
      if (searchParams.get('citySlug')) {
        city = cities.find(c => c.slug === searchParams.get('citySlug'));
      } else if (searchParams.get('city')) {
        city = cities.find(c => c.name === searchParams.get('city'));
      }
      
      if (city && !selectedCity) {
        setSelectedCity(city.id);
        setCityInput(city.name);
        setPage(1);
        setFilteredVenues([]);
      }
    }
  }, [location.pathname, searchParams, cities]);

  // Parse city from URL pathname or query param
  useEffect(() => {
    loadCities();
    loadAdsenseSettings();
  }, []);

  // Restore scroll position when navigating back
  useEffect(() => {
    // Check if we have a saved scroll position
    const savedScroll = sessionStorage.getItem('venuesPageScroll');
    
    if (savedScroll && !loading && filteredVenues.length > 0) {
      const scrollPos = parseInt(savedScroll, 10);
      
      // Wait for content to fully render, then restore scroll
      // Use multiple attempts to ensure content is loaded
      const restoreAttempts = [100, 300, 500];
      
      restoreAttempts.forEach((delay) => {
        setTimeout(() => {
          window.scrollTo(0, scrollPos);
        }, delay);
      });
      
      // Clean up after last attempt
      setTimeout(() => {
        sessionStorage.removeItem('venuesPageScroll');
        console.log(`🔄 Scroll restored to: ${scrollPos}px`);
      }, 600);
    }
  }, [loading, filteredVenues.length]);

  useEffect(() => {
    if (selectedCity) {
      loadDistricts(selectedCity);
    } else {
      setDistricts([]);
    }
  }, [selectedCity]);

  // Sync URL with filter state ONLY on /davet-evleri page (not on city-specific pages)
  useEffect(() => {
    // Only sync URL params on the main /davet-evleri page
    // City-specific pages like /bursa-davet-evleri should have clean URLs
    if (location.pathname !== '/davet-evleri') {
      return; // Skip URL sync for city-specific pages
    }
    
    const params = new URLSearchParams();
    if (selectedCity) {
      params.set('cityId', selectedCity);
    }
    if (selectedDistricts.length > 0) {
      params.set('districtIds', selectedDistricts.join(','));
    }
    
    // Update URL without causing navigation
    const newSearch = params.toString();
    const newUrl = newSearch ? `?${newSearch}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [selectedCity, selectedDistricts, location.pathname]);

  useEffect(() => {
    // Skip loading if we're on a city page and the city hasn't been parsed yet
    if (location.pathname.endsWith('-davet-evleri') && location.pathname !== '/davet-evleri' && !selectedCity) {
      // City page but selectedCity not set yet - wait for slug parsing
      return;
    }
    
    // Reset page and venues when filters change
    setPage(1);
    setFilteredVenues([]);
    setHasMore(true);
    loadVenues(1, true);
  }, [selectedCity, selectedDistricts, location.pathname]);
  
  // Infinite scroll - Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadMoreVenues();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasMore, loading, loadingMore, page]);

  const loadCities = async () => {
    try {
      const response = await getCities();
      setCities(response.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadDistricts = async (cityId) => {
    try {
      // Use getDistrictsWithVenues to only show districts that have venues
      const response = await getDistrictsWithVenues(cityId);
      setDistricts(response.data);
    } catch (error) {
      console.error('Error loading districts:', error);
      setDistricts([]);
    }
  };


  const loadAdsenseSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/adsense-settings`);
      if (response.data.success) {
        setAdsenseSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading AdSense settings:', error);
    }
  };


  const loadVenues = async (pageNum = 1, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = {
        limit: ITEMS_PER_PAGE,
        skip: (pageNum - 1) * ITEMS_PER_PAGE
      };
      
      if (selectedCity) params.cityId = selectedCity;
      
      // Send selected districts to backend (multiple districts support)
      if (selectedDistricts.length > 0) {
        params.districtIds = selectedDistricts.join(',');
      }

      const response = await getVenues(params);
      
      // Handle new backend format with total count
      const venuesData = response.data?.data || response.data || [];
      const totalCount = response.data?.total;
      
      // Always update total count from backend (changes with filters)
      if (totalCount !== undefined) {
        setTotalVenues(totalCount);
      }
      
      if (reset) {
        setFilteredVenues(Array.isArray(venuesData) ? venuesData : []);
      } else {
        // FIXED: Prevent duplicates when loading more venues
        setFilteredVenues(prev => {
          const existingIds = new Set(prev.map(v => v.id || v._id));
          const newVenues = (Array.isArray(venuesData) ? venuesData : [])
            .filter(v => !existingIds.has(v.id || v._id));
          return [...prev, ...newVenues];
        });
      }
      
      // Check if there are more venues to load based on total count
      const currentTotal = reset ? venuesData.length : (prev => prev.length + venuesData.length);
      const loadedCount = reset ? venuesData.length : filteredVenues.length + venuesData.length;
      
      // Use total count from backend to determine if there are more venues
      if (totalCount !== undefined && loadedCount >= totalCount) {
        setHasMore(false);
      } else if (venuesData.length < ITEMS_PER_PAGE) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      console.error('Error loading venues:', error);
      if (reset) {
        setFilteredVenues([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const loadMoreVenues = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadVenues(nextPage, false);
  };

  // City autocomplete
  const handleCityInputChange = (value) => {
    setCityInput(value);
    setShowCitySuggestions(true);
    
    if (value.trim() === '') {
      setFilteredCities([]);
      setSelectedCity('');
      setSelectedDistricts([]); // Fixed: was setSelectedDistrict (singular)
      setDistrictInput('');
      setDistricts([]);
    } else {
      const filtered = cities.filter(city => 
        city.name.toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR'))
      );
      setFilteredCities(filtered);
    }
  };

  const handleCitySelect = async (city) => {
    setCityInput(city.name);
    setSelectedCity(city.id);
    setShowCitySuggestions(false);
    setDistrictInput('');
    setSelectedDistricts([]); // Clear multi-select
    setPage(1); // Fixed: was setCurrentPage, should be setPage
    await loadDistricts(city.id);
  };

  const clearCity = () => {
    setCityInput('');
    setSelectedCity('');
    setSelectedDistricts([]);
    setDistrictInput('');
    setDistricts([]);
    setShowCitySuggestions(false);
  };

  // District autocomplete
  const handleDistrictInputChange = (value) => {
    setDistrictInput(value);
    setShowDistrictSuggestions(true);
    
    if (value.trim() === '') {
      setFilteredDistricts([]);
    } else {
      const filtered = districts.filter(district => 
        district.name.toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR'))
      );
      setFilteredDistricts(filtered);
    }
  };

  const handleDistrictSelect = (district) => {
    setDistrictInput(district.name);
    // Multi-select: toggle district
    const newSelectedDistricts = selectedDistricts.includes(district.id)
      ? selectedDistricts.filter(id => id !== district.id)
      : [...selectedDistricts, district.id];
    
    setSelectedDistricts(newSelectedDistricts);
    
    // Update URL with selected districts
    const newSearchParams = new URLSearchParams(searchParams);
    if (newSelectedDistricts.length > 0) {
      newSearchParams.set('districtIds', newSelectedDistricts.join(','));
    } else {
      newSearchParams.delete('districtIds');
    }
    setSearchParams(newSearchParams);
    
    setShowDistrictSuggestions(false);
  };

  const clearDistrict = () => {
    setDistrictInput('');
    setSelectedDistricts([]);
    setShowDistrictSuggestions(false);
    
    // Remove districtIds from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('districtIds');
    setSearchParams(newSearchParams);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityInputRef.current && !cityInputRef.current.contains(event.target)) {
        setShowCitySuggestions(false);
      }
      if (districtInputRef.current && !districtInputRef.current.contains(event.target)) {
        setShowDistrictSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearFilters = () => {
    setSelectedCity('');
    setCityInput('');
    setSelectedDistricts([]);
    setDistrictInput('');
    setDistricts([]);
    setPage(1);
    setFilteredVenues([]);
    setHasMore(true);
  };

  // SEO Meta Tags
  const selectedCityName = selectedCity ? cities.find(c => c.id === selectedCity)?.name : '';
  const selectedDistrictNames = selectedDistricts.length > 0 
    ? districts.filter(d => selectedDistricts.includes(d.id)).map(d => d.name).join(', ')
    : '';
  
  const pageTitle = selectedCityName 
    ? `${selectedCityName} Söz, Nişan, Kına ve Kurumsal Toplantı Mekanları | Tüm Etkinlik Mekanları - Davetevibul.com`
    : 'Tüm Davet Evleri ve Organizasyon Salonları | DavetEviBul';
  
  const pageDescription = selectedCityName
    ? `${selectedCityName}'da söz, nişan, kız isteme, kına, doğum günü ve kurumsal toplantılarınız için en uygun davet evleri. Mekanları filtrele ve ücretsiz fiyat teklifi al.`
    : `Türkiye'nin tüm illerinde ${filteredVenues.length}+ davet evi ve organizasyon salonu. İstanbul, Ankara, İzmir, Bursa ve tüm şehirlerde en uygun fiyatlarla mekan kiralama.`;
  
  const pageKeywords = selectedCityName
    ? `${selectedCityName} davet evi, ${selectedCityName} söz mekanı, ${selectedCityName} nişan salonu, ${selectedCityName} kına organizasyonu, ${selectedCityName} kurumsal toplantı mekanı, ${selectedCityName} etkinlik mekanı, ${selectedCityName} kız isteme mekanı, ${selectedCityName} doğum günü organizasyonu`
    : 'davet evi, organizasyon salonu, nişan salonu, kına gecesi mekanı, organizasyon mekanı';
  
  const selectedCitySlug = selectedCity ? cities.find(c => c.id === selectedCity)?.slug : '';
  const canonicalUrl = selectedCitySlug 
    ? `${process.env.REACT_APP_FRONTEND_URL || 'https://davetevibul.com'}/${selectedCitySlug}-davet-evleri`
    : `${process.env.REACT_APP_FRONTEND_URL || 'https://davetevibul.com'}/davet-evleri`;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={`${process.env.REACT_APP_FRONTEND_URL || 'https://davetevibul.com'}/og-davet-evleri.jpg`} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${process.env.REACT_APP_FRONTEND_URL || 'https://davetevibul.com'}/og-davet-evleri.jpg`} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* ItemList Schema for Rich Snippets */}
        {filteredVenues.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "name": pageTitle,
              "description": pageDescription,
              "url": canonicalUrl,
              "numberOfItems": totalVenues,
              "itemListElement": filteredVenues.slice(0, 10).map((venue, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                  "@type": "LocalBusiness",
                  "name": venue.name,
                  "url": `https://davetevibul.com/${venue.citySlug || 'city'}/${venue.districtSlug || 'district'}/${venue.slug}`,
                  "image": venue.images?.[0] || '',
                  "address": {
                    "@type": "PostalAddress",
                    "addressLocality": venue.districtName || '',
                    "addressRegion": venue.cityName || '',
                    "addressCountry": "TR"
                  },
                  ...(venue.rating && venue.reviewCount > 0 && { 
                    "aggregateRating": {
                      "@type": "AggregateRating",
                      "ratingValue": String(venue.rating),
                      "reviewCount": String(venue.reviewCount || 1),
                      "bestRating": "5",
                      "worstRating": "1"
                    }
                  }),
                  ...(venue.capacity?.min && venue.capacity?.max && {
                    "maximumAttendeeCapacity": venue.capacity.max
                  })
                }
              }))
            })}
          </script>
        )}
      </Helmet>

      {/* Search Header */}
      <div className="bg-gradient-to-r from-pink-600 to-rose-600 text-white py-6 sm:py-8">
        <div className="container mx-auto px-3 sm:px-4">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
            {selectedCity && cities.find(c => c.id === selectedCity)?.name 
              ? `${cities.find(c => c.id === selectedCity)?.name} - Davet Evleri` 
              : 'Davet Evlerini Keşfedin'}
          </h1>
          
          <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4">
            <div className="flex flex-col gap-3 sm:gap-4">
              {/* City Selection */}
              <div className="flex gap-3">
                <div className="flex-1 relative" ref={cityInputRef}>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 z-10" />
                    <Input
                      value={cityInput}
                      onChange={(e) => handleCityInputChange(e.target.value)}
                      onFocus={() => {
                        setShowCitySuggestions(true);
                        if (cityInput) {
                          const filtered = cities.filter(city => 
                            city.name.toLocaleLowerCase('tr-TR').includes(cityInput.toLocaleLowerCase('tr-TR'))
                          );
                          setFilteredCities(filtered);
                        } else {
                          setFilteredCities(cities);
                        }
                      }}
                      placeholder="İl değiştir (örn: Bursa)"
                      className="h-12 pl-10 pr-10 text-gray-900"
                    />
                    {cityInput && (
                      <button
                        onClick={clearCity}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {showCitySuggestions && (cityInput ? filteredCities : cities).length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {(cityInput ? filteredCities : cities).map(city => (
                        <button
                          key={city.id}
                          onClick={() => handleCitySelect(city)}
                          className="w-full text-left px-4 py-2 hover:bg-pink-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center gap-2 text-gray-900">
                            <MapPin className="w-4 h-4 text-pink-600" />
                            <span className="font-medium">{city.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* District Filter - Classic Checkbox Design */}
              {selectedCity && districts.length > 0 && (
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800">İlçe Seçin</span>
                    {selectedDistricts.length > 0 && (
                      <button
                        onClick={() => {
                          setSelectedDistricts([]);
                          const newSearchParams = new URLSearchParams(searchParams);
                          newSearchParams.delete('districtIds');
                          setSearchParams(newSearchParams);
                        }}
                        className="text-xs text-pink-600 hover:text-pink-700 font-medium"
                      >
                        Temizle ({selectedDistricts.length})
                      </button>
                    )}
                  </div>
                  
                  {/* Checkbox Grid - Max 4 rows visible with scroll */}
                  <div className="max-h-[140px] overflow-y-auto pr-2 border border-gray-100 rounded-lg p-2 bg-gray-50/50">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                      {districts.map(district => {
                        const isSelected = selectedDistricts.includes(district.id);
                        return (
                          <label
                            key={district.id}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-all text-sm ${
                              isSelected 
                                ? 'bg-pink-100 text-pink-700 font-medium' 
                                : 'bg-white hover:bg-pink-50 text-gray-600'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                const newSelectedDistricts = isSelected
                                  ? selectedDistricts.filter(id => id !== district.id)
                                  : [...selectedDistricts, district.id];
                                
                                setSelectedDistricts(newSelectedDistricts);
                                
                                const newSearchParams = new URLSearchParams(searchParams);
                                if (newSelectedDistricts.length > 0) {
                                  newSearchParams.set('districtIds', newSelectedDistricts.join(','));
                                } else {
                                  newSearchParams.delete('districtIds');
                                }
                                setSearchParams(newSearchParams);
                              }}
                              className="w-3.5 h-3.5 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                            />
                            <span className="truncate text-xs">{district.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Scroll hint if many districts */}
                  {districts.length > 12 && (
                    <p className="text-xs text-gray-400 text-center">↓ Daha fazla ilçe için kaydırın</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 md:px-6 py-8">
        <div>
          {/* Venues Grid */}
          <div>
            {!loading && (
              <div className="mb-6 flex items-center justify-between">
                <p className="text-gray-600">
                  <span className="font-semibold text-gray-900">{totalVenues > 0 ? totalVenues : filteredVenues.length}</span> davet evi bulundu
                </p>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
              </div>
            ) : filteredVenues.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Sonuç Bulunamadı</h3>
                  <p className="text-gray-600">Arama kriterlerinizi değiştirerek tekrar deneyin</p>
                  <Button onClick={clearFilters} className="mt-4">Filtreleri Temizle</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                {filteredVenues.map((venue, venueIndex) => {
                  const hasCampaign = venue.activeCampaigns && venue.activeCampaigns.length > 0;
                  
                  // Show ad after every N venues (based on adsense_frequency)
                  const shouldShowAd = adsenseSettings.adsense_enabled && 
                                      adsenseSettings.adsense_code && 
                                      (venueIndex + 1) % adsenseSettings.adsense_frequency === 0;
                  
                  return (
                  <React.Fragment key={venue.id}>
                  {/* Yeni Minimal Kart Tasarımı */}
                  <VenueCard 
                    venue={venue}
                    navigate={handleVenueNavigate}
                    onAuthRequired={handleAuthRequired}
                    venueIndex={venueIndex}
                    
                  />
                  
                  {/* Show AdSense Ad after every N cards */}
                  {shouldShowAd && (
                    <div className="col-span-1 md:col-span-2 lg:col-span-3 w-full">
                      <AdSenseAd adCode={adsenseSettings.adsense_code} />
                    </div>
                  )}
                  </React.Fragment>
                  );
                })}
              </div>
            )}
            
            {/* Skeleton Loading State */}
            {loading && filteredVenues.length === 0 && (
              <VenueSkeletonGrid count={9} />
            )}
            
            {/* Infinite Scroll Trigger & Loading Indicator */}
            {filteredVenues.length > 0 && (
              <div className="mt-8">
                {hasMore && (
                  <div ref={loadMoreRef} className="flex flex-col justify-center items-center py-8 gap-4">
                    {loadingMore ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
                        <p className="text-sm text-gray-500">Daha fazla mekan yükleniyor...</p>
                      </div>
                    ) : (
                      <button 
                        onClick={loadMoreVenues}
                        className="px-6 py-3 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-full font-medium hover:from-pink-600 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl"
                        data-testid="load-more-btn"
                      >
                        Daha Fazla Göster ({filteredVenues.length}/{totalVenues})
                      </button>
                    )}
                  </div>
                )}
                {!hasMore && filteredVenues.length > 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">✨ Tüm mekanlar yüklendi</p>
                    <p className="text-sm text-gray-400 mt-1">Toplam {filteredVenues.length} mekan gösteriliyor</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quote Request Dialog */}
      {selectedVenueForQuote && (
        <QuoteRequestDialog
          open={quoteDialogOpen}
          onOpenChange={setQuoteDialogOpen}
          venue={selectedVenueForQuote}
        />
      )}

      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
};

export default VenuesPage;
